import type { SlideRubric } from '@breakoutlearning/firebase-repository/models/SlideRubric'
import type { SlideDeckRole } from '@breakoutlearning/firebase-repository/models/SlideDeckRole'
import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { BreakoutTextInput } from 'components/design-system/BreakoutTextInput'
import { InlineDialog } from 'components/dialogs/Dialog'
import { DialogCloseButton } from 'components/dialogs/DialogCloseButton'
import { CheckIcon } from 'components/icons/Check'
import { PercentageIcon } from 'components/icons/Percentage'
import { TriangleAlertIcon } from 'components/icons/TriangleAlert'
import classNames from 'classnames'
import { useCallback, useId, useMemo, useState } from 'react'
import { useTranslationTyped } from 'i18n/i18n'
import {
  ALL_ROLES_COLUMN,
  columnSum,
  equalSplit,
  rubricAppliesToColumn,
  type WeightColumn,
  type WeightGrid,
} from './customRubricsConfig'

/**
 * Modal for customising per-rubric weights. One row per contribution rubric;
 * when the deck has roles each role is a tab and every role's weights must total
 * 100% independently. The score mapping stays in the main grading dialog.
 */
export function ConfigureWeightsDialog({
  rubrics,
  roles,
  initialGrid,
  disabled,
  onSave,
  onClose,
}: {
  rubrics: SlideRubric[]
  roles: SlideDeckRole[]
  initialGrid: WeightGrid
  disabled?: boolean
  onSave: (grid: WeightGrid) => void
  onClose: () => void
}) {
  const { tt } = useTranslationTyped()
  const headingId = useId()

  const hasRoles = roles.length > 0
  const columns: WeightColumn[] = useMemo(() => {
    if (!hasRoles) {
      return [
        {
          key: ALL_ROLES_COLUMN,
          label: tt.instructor_library.custom_rubrics_all_roles(),
        },
      ]
    }
    return roles.map((role) => ({ key: role.id, label: role.data.roleName }))
  }, [roles, hasRoles, tt])

  const [grid, setGrid] = useState<WeightGrid>(initialGrid)
  const [activeColIndex, setActiveColIndex] = useState(0)
  const activeCol = columns[activeColIndex] ?? columns[0]

  const handleChange = useCallback(
    (rubricId: string, columnKey: string, raw: string) => {
      const parsed = raw ? Number.parseInt(raw) : 0
      if (Number.isNaN(parsed) || parsed < 0 || parsed > 100) return
      setGrid((prev) => ({
        ...prev,
        [rubricId]: { ...prev[rubricId], [columnKey]: parsed },
      }))
    },
    []
  )

  const resetToDefaults = useCallback(() => {
    setGrid(() => {
      const next: WeightGrid = {}
      for (const rubric of rubrics) {
        next[rubric.id] = {}
        for (const col of columns) next[rubric.id][col.key] = 0
      }
      // Per column, split 100% across only the rubrics applicable to that role.
      for (const col of columns) {
        const applicable = rubrics.filter((r) =>
          rubricAppliesToColumn(r, col.key, roles)
        )
        const split = equalSplit(100, applicable.length)
        applicable.forEach((rubric, i) => {
          next[rubric.id][col.key] = split[i]
        })
      }
      return next
    })
  }, [rubrics, columns, roles])

  // Only the rubrics that apply to the active role are shown/weighted; the rest
  // don't contribute to that role's 100%.
  const visibleRubrics = useMemo(
    () => rubrics.filter((r) => rubricAppliesToColumn(r, activeCol.key, roles)),
    [rubrics, activeCol.key, roles]
  )

  const colValid = (columnKey: string) =>
    columnSum(grid, columnKey, rubrics, roles) === 100
  const allValid = columns.every((col) => colValid(col.key))
  const activeTotal = columnSum(grid, activeCol.key, rubrics, roles)
  const activeValid = activeTotal === 100

  // a role other than the active one still needs attention
  const otherInvalidIndex = columns.findIndex(
    (col, i) => i !== activeColIndex && !colValid(col.key)
  )
  const showNextRole = hasRoles && otherInvalidIndex !== -1

  const handlePrimary = useCallback(() => {
    if (showNextRole) {
      setActiveColIndex(otherInvalidIndex)
      return
    }
    if (!allValid) return
    onSave(grid)
  }, [showNextRole, otherInvalidIndex, allValid, onSave, grid])

  return (
    <InlineDialog
      size="lg"
      open
      ignoreBackdropClick
      onDismiss={onClose}
      innerClassName="flex flex-col gap-4"
      aria-labelledby={headingId}
    >
      <DialogCloseButton
        onClick={(e) => {
          e.preventDefault()
          onClose()
          return false
        }}
      />
      <h1 id={headingId} className="text-center text-headline-large">
        {tt.instructor_library.custom_rubrics_weights_dialog_title()}
      </h1>
      <p className="text-body-medium text-on-surface-var">
        {tt.instructor_library.custom_rubrics_weights_dialog_description()}
      </p>

      {hasRoles && (
        <div className="flex flex-row items-center gap-2 rounded-xl border border-fixed-accent-color px-3 py-2">
          <TriangleAlertIcon size={18} className="stroke-fixed-accent-color" />
          <div className="text-label-medium">
            {tt.instructor_library.custom_rubrics_weights_roles_warning()}
          </div>
        </div>
      )}

      {hasRoles && (
        <div className="flex flex-row items-center gap-3">
          <strong className="text-label-large">
            {tt.instructor_library.custom_rubrics_weights_roles_label()}
          </strong>
          <div className="flex flex-row gap-2 rounded-2xl border border-outline-light bg-white p-2">
            {columns.map((col, i) => (
              <BreakoutButton
                key={col.key}
                className="!min-w-0"
                size="medium"
                kind={i === activeColIndex ? 'accent' : 'secondary'}
                onClick={() => setActiveColIndex(i)}
                data-testid={`weights-role-tab-${col.key}`}
              >
                <span className="flex flex-row items-center gap-1">
                  {colValid(col.key) ? (
                    <CheckIcon size={14} className="text-core-success" />
                  ) : (
                    <TriangleAlertIcon
                      size={14}
                      className="stroke-core-error"
                    />
                  )}
                  {col.label}
                </span>
              </BreakoutButton>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col">
        <div className="flex flex-row justify-between rounded-t-lg bg-surface-dim px-3 py-2">
          <strong className="text-label-medium">
            {tt.instructor_library.custom_rubrics_weights_rubric_header()}
          </strong>
          <strong className="text-label-medium">
            {tt.instructor_library.custom_rubrics_weights_weight_header()}
          </strong>
        </div>

        {visibleRubrics.map((rubric) => (
          <div
            key={rubric.id}
            className="flex flex-row items-center justify-between gap-3 border-b border-outline-light px-3 py-2"
          >
            <div className="flex min-w-0 flex-1 flex-col">
              <strong className="text-label-medium">
                {rubric.data.rubric}
              </strong>
              <div className="text-body-medium text-on-surface-var">
                {rubric.data.rubricDescription}
              </div>
            </div>
            <BreakoutTextInput
              testId={`rubric-weight-${rubric.id}`}
              kind="secondary"
              variant="compact"
              className="w-[3.75rem] shrink-0"
              value={String(grid[rubric.id]?.[activeCol.key] ?? 0)}
              disabled={disabled}
              onChange={(e) =>
                handleChange(rubric.id, activeCol.key, e.target.value)
              }
              type="number"
              min={0}
              max={100}
              TrailingIcon={PercentageIcon}
              iconSize={10}
              iconClassName={'text-on-surface-var !right-[15px]'}
              inputClassName="text-right !pr-[26px] pl-2 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />
          </div>
        ))}

        <div className="flex flex-row justify-between px-3 py-2">
          <strong className="text-title-medium">
            {tt.instructor_library.custom_rubrics_weights_total()}
          </strong>
          <strong
            className={classNames('text-title-medium', {
              'text-core-error': !activeValid,
            })}
            data-testid="rubric-weights-total"
          >
            {activeTotal}%
          </strong>
        </div>

        <div className="flex flex-row items-center justify-between rounded-b-lg bg-surface-dim px-3 py-2">
          <div className="text-label-medium text-on-surface-var">
            {tt.instructor_library.custom_rubrics_weights_total_hint()}
          </div>
          <BreakoutButton
            kind="secondary"
            size="small"
            disabled={disabled}
            onClick={resetToDefaults}
            data-testid="reset-weights-defaults"
          >
            {tt.instructor_library.custom_rubrics_reset_defaults()}
          </BreakoutButton>
        </div>
      </div>

      <div className="mt-2 flex flex-row justify-between gap-2">
        <BreakoutButton kind="secondary" onClick={onClose}>
          {tt.instructor_library.cancel()}
        </BreakoutButton>
        <BreakoutButton
          kind="accent"
          disabled={disabled || (!showNextRole && !allValid)}
          onClick={handlePrimary}
          data-testid="save-rubric-weights"
        >
          {showNextRole
            ? tt.instructor_library.custom_rubrics_next_role()
            : tt.instructor_library.custom_rubrics_save()}
        </BreakoutButton>
      </div>
    </InlineDialog>
  )
}
